import { Button, Select, Text, toast } from "@medusajs/ui"
import { useEffect, useState } from "react"
import { Field } from "@/components/form"
import { errorMessage } from "@/lib/sdk"
import { useSaveShipStationStore, useShipStationStores } from "@/lib/shipstation"
import { Banner } from "@/terabizz"

const NONE = "__none"

/**
 * Which ShipStation store new orders are created in (v1 advancedOptions.storeId). Without one,
 * ShipStation files them under "Manual Orders" with anything else that lands there. Stores are
 * read live from the ShipStation account; the store itself is added in ShipStation.
 */
export function ShipStationStoreSetting({ manage }: { manage: boolean }) {
  const { data, error, isLoading, refetch, isFetching } = useShipStationStores(true)
  const save = useSaveShipStationStore()
  const d = data?.stores
  const [value, setValue] = useState<string>(NONE)
  useEffect(() => {
    if (d) setValue(d.store_id != null ? String(d.store_id) : NONE)
  }, [d])

  if (error) return <Text size="small" className="border-t border-ui-border-base px-4 py-3 text-ui-fg-error">{errorMessage(error)}</Text>
  if (isLoading || !d) return <Text size="small" className="border-t border-ui-border-base px-4 py-3 text-ui-fg-subtle">Loading ShipStation stores…</Text>
  if (!d.supported) {
    return (
      <div className="flex flex-col gap-1 border-t border-ui-border-base px-4 py-4">
        <Text size="small" weight="plus">Store</Text>
        <Text size="xsmall" className="text-ui-fg-subtle">{d.error ?? "Choosing a ShipStation store needs the classic v1 API (API key + API secret)."}</Text>
      </div>
    )
  }

  const saved = d.store_id != null ? String(d.store_id) : NONE
  const dirty = value !== saved
  const known = d.stores.some((s) => String(s.storeId) === saved)
  const submit = () =>
    save.mutate(value === NONE ? null : Number(value), {
      onSuccess: (r) => toast.success(r.stores.store_id != null ? `New orders will go to “${r.stores.store_name ?? r.stores.store_id}” in ShipStation` : "New orders will go to Manual Orders in ShipStation"),
      onError: (e) => toast.error(errorMessage(e)),
    })

  return (
    <div className="flex flex-col gap-3 border-t border-ui-border-base px-4 py-4">
      <div>
        <Text size="small" weight="plus">Store</Text>
        <Text size="xsmall" className="text-ui-fg-subtle">
          The ShipStation store new orders are created in, so they don't mix with other orders under Manual Orders. Add the store in ShipStation first (Settings → Selling Channels → Store Setup → Connect a Store or Marketplace → ShipStation), then pick it here.
        </Text>
      </div>
      {d.error && (
        <Banner tone="warning" title="Couldn't load the stores from ShipStation" action={<Button size="small" variant="secondary" isLoading={isFetching} onClick={() => refetch()}>Try again</Button>}>
          {d.error}
        </Banner>
      )}
      <div className="grid items-end gap-2 sm:grid-cols-[1fr_auto]">
        <Field label="Send new orders to">
          <Select value={value} disabled={!manage || save.isPending} onValueChange={setValue}>
            <Select.Trigger><Select.Value /></Select.Trigger>
            <Select.Content>
              <Select.Item value={NONE}>Manual Orders (no store)</Select.Item>
              {d.stores.map((s) => (
                <Select.Item key={s.storeId} value={String(s.storeId)} disabled={!s.active}>
                  {`${s.storeName}${s.marketplaceName ? ` · ${s.marketplaceName}` : ""}${s.active ? "" : " (deactivated)"}`}
                </Select.Item>
              ))}
              {d.store_id != null && !known && <Select.Item value={String(d.store_id)}>{d.store_name ?? `Store ${d.store_id}`}</Select.Item>}
            </Select.Content>
          </Select>
        </Field>
        {manage && (
          <div className="flex gap-2">
            {dirty && <Button size="small" variant="secondary" onClick={() => setValue(saved)}>Discard</Button>}
            <Button size="small" disabled={!dirty} isLoading={save.isPending} onClick={submit}>Save store</Button>
          </div>
        )}
      </div>
      <Text size="xsmall" className="text-ui-fg-subtle">
        Applies to orders sent from now on. Orders already in ShipStation stay in the store they were created in: ShipStation only sets an order's store when the order is created.
      </Text>
    </div>
  )
}
